/**
 * CategorySummarySection.tsx
 *
 * "סיכום לפי סיווג" — Category Summary
 *
 * An **independent** section at the bottom of the Cash-Flow Dashboard.
 * Owns its own data-loading state, completely decoupled from the main
 * dashboard's date filter.
 *
 * Features
 * ────────
 * • Year selector  (defaults to current year, ±3-year range)
 * • Classification toggle: "סיווג תזרים" | "סיווג הנהלת חשבונות"
 * • Doughnut chart (recharts PieChart) — yearly totals per category
 * • 12-month × category matrix table with a "סה״כ" column
 * • Every numerical cell is clickable → opens CategoryDrillDownModal
 *   showing the exact ForecastRows that compose that aggregated value
 * • Income green / Expense red colour coding
 * • Fully RTL, matches the rest of the dashboard's Tailwind design
 */

import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  ChevronDown,
  ChevronRight,
  Loader2,
  BarChart2,
  AlertTriangle,
  Tag,
  BookOpen,
  X,
  Building2,
  Calendar,
  Repeat,
  RefreshCw,
  Search,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import { useCompany } from '../../context/CompanyContext';
import { bankTransactionsService } from '../../lib/bankTransactionsService';
import { knownActionsService } from '../../lib/knownActionsService';
import type { KnownAction } from '../../lib/knownActionsService';
import { recurringActionsService } from '../../lib/recurringActionsService';
import { offsetCodesService } from '../../lib/offsetCodesService';
import { buildOffsetMap } from '../../lib/offsetCalculator';
import { buildUnifiedRows } from '../../lib/rolloverUtils';
import type { ForecastRow } from '../../lib/rolloverUtils';
import type { BankTransaction, RecurringActionLine, OffsetCode } from '../../types/database';

// ─── Constants ────────────────────────────────────────────────────────────────

const MONTH_NAMES_SHORT = [
  'ינו', 'פבר', 'מרץ', 'אפר', 'מאי', 'יונ',
  'יול', 'אוג', 'ספט', 'אוק', 'נוב', 'דצמ',
];

const MONTH_NAMES_LONG = [
  'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
  'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר',
];

const CASHFLOW_LABELS: Record<string, string> = {
  current_business:       'שוטף – פעילות עסקית',
  financing_loan:         'מימון – הלוואה',
  intra_group_loan:       'הלוואה תוך-קבוצתית',
  investment:             'השקעה',
  savings:                'חיסכון',
  taxes:                  'מיסים',
  inter_account_transfer: 'העברות בין חשבונות',
  personal:               'אישי',
  other:                  'אחר',
};

/**
 * Fixed P&L sort order for the "סיווג הנהלת חשבונות" (accounting) mode.
 * Categories listed here appear first in the declared order.
 * Any unlisted category is placed at the bottom, preserving relative order.
 */
const ACCOUNTING_CATEGORY_ORDER: readonly string[] = [
  'הכנסות ממכירות',
  'הוצאות עלות המכר',
  'הוצאות הנהלה וכללי',
  'הוצאות שיווק ומכירה',
  'הוצאות שכר',
  'הוצאות מימון',
  'הלוואה - מימון',
];

/** Returns the priority index for a category (lower = higher in table). */
function accountingCategoryPriority(name: string): number {
  const idx = ACCOUNTING_CATEGORY_ORDER.indexOf(name);
  return idx === -1 ? ACCOUNTING_CATEGORY_ORDER.length : idx;
}

// 16 visually distinct, accessible palette colours (hex)
const PALETTE = [
  '#3b82f6', // blue-500
  '#10b981', // emerald-500
  '#f59e0b', // amber-500
  '#ef4444', // red-500
  '#8b5cf6', // violet-500
  '#06b6d4', // cyan-500
  '#f97316', // orange-500
  '#ec4899', // pink-500
  '#84cc16', // lime-500
  '#14b8a6', // teal-500
  '#6366f1', // indigo-500
  '#a3a3a3', // neutral-400
  '#eab308', // yellow-500
  '#0ea5e9', // sky-500
  '#d946ef', // fuchsia-500
  '#22c55e', // green-500
];

// ─── Currency formatters ──────────────────────────────────────────────────────

const ILS = new Intl.NumberFormat('he-IL', {
  style: 'currency', currency: 'ILS',
  minimumFractionDigits: 0, maximumFractionDigits: 0,
});

const ILS2 = new Intl.NumberFormat('he-IL', {
  style: 'currency', currency: 'ILS',
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

// ─── Shared helpers ───────────────────────────────────────────────────────────

const amountClass = (v: number) =>
  v > 0 ? 'text-emerald-700' : v < 0 ? 'text-red-700' : 'text-gray-300';

// Format YYYY-MM-DD → DD/MM/YYYY
const fmtDate = (d: string) => d.split('-').reverse().join('/');

// ─── Types ────────────────────────────────────────────────────────────────────

type ClassificationMode = 'cashflow' | 'accounting';

/** Which slice of rows the drill-down modal should show and label */
type RowFilter = 'all' | 'income' | 'expense';

/**
 * The drill-down context.
 * `rows` always contains ALL rows for the cell; the modal applies
 * `rowFilter` itself so the header label and search operate on the
 * correct subset.
 */
interface DrillTarget {
  category:   string;               // display label
  monthIndex: number | null;        // 0-11, or null for yearly total
  rows:       ForecastRow[];        // ALL rows for this category×month cell
  rowFilter:  RowFilter;            // which sign to show inside the modal
  year:       number;
  color:      string;
}

/** One enriched row in the matrix */
interface MatrixRow {
  category:    string;
  months:      number[];            // [0..11] net
  incomeByMonth:  number[];         // [0..11] positive sums only
  expenseByMonth: number[];         // [0..11] negative sums only
  total:       number;              // net yearly
  incomeTotal: number;              // gross income yearly
  expenseTotal: number;             // gross expense yearly
  colorIndex:  number;
  /** The actual ForecastRows bucketed by month index (for drill-down) */
  rowsByMonth: ForecastRow[][];     // [0..11]
  allRows:     ForecastRow[];       // all rows for this category (yearly)
}

interface PieSlice {
  name:  string;
  value: number;   // absolute value for pie sizing
  raw:   number;   // real signed amount (for tooltip)
  color: string;
}

// ─── Year range ───────────────────────────────────────────────────────────────

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 7 }, (_, i) => CURRENT_YEAR - 3 + i);

// ─── SourceType badge ─────────────────────────────────────────────────────────

function SourceBadge({ type }: { type: ForecastRow['sourceType'] }) {
  const configs = {
    Bank:       { icon: <Building2 className="w-2.5 h-2.5" />, label: 'בנק',   cls: 'bg-blue-100 text-blue-800 border-blue-200' },
    Known:      { icon: <Calendar   className="w-2.5 h-2.5" />, label: 'ידוע',  cls: 'bg-slate-100 text-slate-700 border-slate-200' },
    Recurring:  { icon: <Repeat     className="w-2.5 h-2.5" />, label: 'קבוע',  cls: 'bg-purple-100 text-purple-800 border-purple-200' },
    RolledOver: { icon: <RefreshCw  className="w-2.5 h-2.5" />, label: 'מגולגל', cls: 'bg-red-100 text-red-700 border-red-200' },
  };
  const cfg = configs[type];
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold border whitespace-nowrap ${cfg.cls}`}>
      {cfg.icon}
      {cfg.label}
    </span>
  );
}

// ─── Pie Tooltip ──────────────────────────────────────────────────────────────

interface PieTooltipProps {
  active?:  boolean;
  payload?: Array<{ payload: PieSlice }>;
}

function PieTooltipContent({ active, payload }: PieTooltipProps) {
  if (!active || !payload?.length) return null;
  const slice = payload[0]?.payload;
  if (!slice) return null;
  return (
    <div
      className="pointer-events-none bg-white border border-gray-200 rounded-xl shadow-xl px-4 py-3 text-right min-w-[180px]"
      dir="rtl"
    >
      <p className="text-xs font-semibold text-gray-700 mb-1">{slice.name}</p>
      <p className={`text-sm font-bold tabular-nums ${slice.raw >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
        {ILS2.format(slice.raw)}
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CategoryDrillDownModal
// ─────────────────────────────────────────────────────────────────────────────

interface DrillModalProps {
  target:  DrillTarget | null;
  onClose: () => void;
}

function CategoryDrillDownModal({ target, onClose }: DrillModalProps) {
  const [search, setSearch] = useState('');

  // Reset search when target changes
  useEffect(() => { setSearch(''); }, [target]);

  // Close on Escape
  useEffect(() => {
    if (!target) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [target, onClose]);

  if (!target) return null;

  const { category, monthIndex, rows, rowFilter, year, color } = target;

  // Apply the row-filter (income / expense / all) BEFORE search
  const filteredBySign: ForecastRow[] =
    rowFilter === 'income'  ? rows.filter(r => r.amount > 0) :
    rowFilter === 'expense' ? rows.filter(r => r.amount < 0) :
    rows;

  // Period label
  const periodLabel = monthIndex === null
    ? `כל ${year}`
    : `${MONTH_NAMES_LONG[monthIndex] ?? ''} ${year}`;

  // Row-filter badge text + colour
  const filterBadge =
    rowFilter === 'income'  ? { label: 'הכנסות בלבד',    cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' } :
    rowFilter === 'expense' ? { label: 'הוצאות בלבד',    cls: 'bg-red-100 text-red-700 border-red-200' } :
                              { label: 'כל התנועות',     cls: 'bg-blue-100 text-blue-700 border-blue-200' };

  // Filter by search
  const q = search.trim().toLowerCase();
  const visible = q
    ? filteredBySign.filter(r =>
        r.description.toLowerCase().includes(q)            ||
        r.bankAccountNickname.toLowerCase().includes(q)    ||
        r.accountingCategoryName.toLowerCase().includes(q) ||
        fmtDate(r.effectiveDate).includes(q)               ||
        Math.abs(r.amount).toFixed(0).includes(q)
      )
    : filteredBySign;

  // Sort by effectiveDate ASC then Bank first on ties
  const sorted = [...visible].sort((a, b) => {
    const d = a.effectiveDate.localeCompare(b.effectiveDate);
    if (d !== 0) return d;
    return (a.sourceType === 'Bank' ? 0 : 1) - (b.sourceType === 'Bank' ? 0 : 1);
  });

  const total = sorted.reduce((s, r) => s + r.amount, 0);

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      dir="rtl"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Panel */}
      <div
        className="bg-white rounded-2xl shadow-2xl flex flex-col"
        style={{ width: '90vw', maxWidth: 900, maxHeight: '88vh', minHeight: 320 }}
      >
        {/* ── Header ── */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            {/* Category colour dot */}
            <span
              className="inline-block w-3.5 h-3.5 rounded-full flex-shrink-0 mt-0.5"
              style={{ backgroundColor: color }}
            />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-base font-bold text-gray-900 leading-tight truncate" title={category}>
                  {category}
                </h2>
                {/* Filter badge */}
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border whitespace-nowrap ${filterBadge.cls}`}>
                  {rowFilter === 'income'  && <TrendingUp   className="w-3 h-3" />}
                  {rowFilter === 'expense' && <TrendingDown  className="w-3 h-3" />}
                  {filterBadge.label}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                {periodLabel}
                &nbsp;·&nbsp;
                <span className={`font-semibold tabular-nums ${amountClass(total)}`}>
                  {ILS2.format(total)}
                </span>
                &nbsp;·&nbsp;
                {sorted.length} תנועות
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            aria-label="סגור"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ── Search bar ── */}
        <div className="px-5 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="relative max-w-xs">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="חיפוש תנועה..."
              className="w-full rounded-lg border border-gray-200 bg-gray-50 pr-8 pl-3 py-1.5
                text-sm text-gray-800 placeholder-gray-400
                focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
            />
          </div>
        </div>

        {/* ── Table ── */}
        <div className="flex-1 overflow-y-auto overflow-x-auto min-h-0">
          {sorted.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-gray-400 gap-2">
              <Search className="w-8 h-8 text-gray-300" />
              <p className="text-sm">לא נמצאו תנועות</p>
            </div>
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 whitespace-nowrap">תאריך</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500">תיאור</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 whitespace-nowrap">חשבון</th>
                  <th className="px-4 py-2.5 text-center text-xs font-semibold text-gray-500 whitespace-nowrap">סוג</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 whitespace-nowrap">סכום</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sorted.map((row, i) => (
                  <tr
                    key={row.key}
                    className={`transition-colors hover:bg-blue-50/30 ${
                      i % 2 === 1 ? 'bg-gray-50/40' : 'bg-white'
                    }`}
                  >
                    {/* Date */}
                    <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap font-mono text-xs">
                      {fmtDate(row.effectiveDate)}
                    </td>

                    {/* Description */}
                    <td className="px-4 py-2.5 text-gray-800 max-w-[240px]">
                      <div className="truncate font-medium" title={row.description}>
                        {row.description}
                      </div>
                      {row.accountingCategoryName && row.accountingCategoryName !== '—' && (
                        <div className="text-[10px] text-gray-400 truncate mt-0.5">
                          {row.accountingCategoryName}
                        </div>
                      )}
                    </td>

                    {/* Bank account */}
                    <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap text-xs">
                      {row.bankAccountNickname !== '—' ? row.bankAccountNickname : '—'}
                    </td>

                    {/* Source type badge */}
                    <td className="px-4 py-2.5 text-center">
                      <SourceBadge type={row.sourceType} />
                    </td>

                    {/* Amount */}
                    <td className={`px-4 py-2.5 text-left tabular-nums font-bold whitespace-nowrap ${amountClass(row.amount)}`}>
                      {ILS2.format(row.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>

              {/* ── Summary footer ── */}
              <tfoot className="sticky bottom-0 z-10">
                <tr className="border-t-2 border-gray-200 bg-gray-50">
                  <td colSpan={4} className="px-4 py-2.5 text-right text-xs font-bold text-gray-600">
                    סה״כ ({sorted.length} תנועות{q ? ` – מסונן` : ''})
                  </td>
                  <td className={`px-4 py-2.5 text-left tabular-nums text-sm font-extrabold ${amountClass(total)}`}>
                    {ILS2.format(total)}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        {/* ── Footer buttons ── */}
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-gray-100 flex-shrink-0 bg-gray-50/60">
          <p className="text-[11px] text-gray-400">
            לחץ על הרקע או על ✕ לסגירה
          </p>
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold
              hover:bg-gray-700 transition-colors"
          >
            סגור
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CategorySummarySection
// ─────────────────────────────────────────────────────────────────────────────

export function CategorySummarySection() {
  const { activeCompanyId } = useCompany();

  // ── Independent state ─────────────────────────────────────────────────────
  const [selectedYear,   setSelectedYear]   = useState<number>(CURRENT_YEAR);
  const [classMode,      setClassMode]      = useState<ClassificationMode>('cashflow');
  const [loading,        setLoading]        = useState(false);
  const [errorMsg,       setErrorMsg]       = useState<string | null>(null);
  const [refreshKey,     setRefreshKey]     = useState(0);

  // ── Drill-down state ──────────────────────────────────────────────────────
  const [drillTarget, setDrillTarget] = useState<DrillTarget | null>(null);

  // ── Raw data ──────────────────────────────────────────────────────────────
  const [bankTxns,       setBankTxns]       = useState<BankTransaction[]>([]);
  const [knownActions,   setKnownActions]   = useState<KnownAction[]>([]);
  const [recurringLines, setRecurringLines] = useState<RecurringActionLine[]>([]);
  const [offsetCodes,    setOffsetCodes]    = useState<OffsetCode[]>([]);

  // ── Load all raw data ─────────────────────────────────────────────────────
  const loadAll = useCallback(async () => {
    if (!activeCompanyId) return;
    setLoading(true);
    setErrorMsg(null);
    try {
      const [bankRes, knownRes, recurringRes, offsetRes] = await Promise.all([
        bankTransactionsService.getBankTransactions(activeCompanyId),
        knownActionsService.getKnownActions(activeCompanyId),
        recurringActionsService.getRecurringActions(activeCompanyId),
        offsetCodesService.getOffsetCodes(activeCompanyId),
      ]);
      setBankTxns      (bankRes.data      ?? []);
      setKnownActions  (knownRes.data     ?? []);
      setRecurringLines(recurringRes.data ?? []);
      setOffsetCodes   (offsetRes.data    ?? []);
    } catch (err) {
      console.error('CategorySummarySection load error:', err);
      setErrorMsg('שגיאה בטעינת נתוני הסיכום. נסה לרענן.');
    } finally {
      setLoading(false);
    }
  }, [activeCompanyId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Build unified rows (Bank-is-Truth pipeline) ───────────────────────────
  const offsetMap = useMemo(
    () => buildOffsetMap(recurringLines, knownActions, offsetCodes),
    [recurringLines, knownActions, offsetCodes],
  );

  /** Full unified dataset — ALL rows for this company, no date filter */
  const allRows = useMemo(
    () => buildUnifiedRows({ bankTxns, knownActions, recurringLines, offsetMap }),
    [bankTxns, knownActions, recurringLines, offsetMap],
  );

  // ── Filter to selected year ───────────────────────────────────────────────
  const yearStart = `${selectedYear}-01-01`;
  const yearEnd   = `${selectedYear}-12-31`;

  const yearRows = useMemo(
    () => allRows.filter(r => r.effectiveDate >= yearStart && r.effectiveDate <= yearEnd),
    [allRows, yearStart, yearEnd],
  );

  // ── Classification label helper ───────────────────────────────────────────
  const labelOf = useCallback(
    (row: ForecastRow): string => {
      if (classMode === 'cashflow') {
        const key = row.cashflowMapping;
        if (!key) return 'לא מסווג';
        return CASHFLOW_LABELS[key as string] ?? String(key);
      }
      const name = row.accountingCategoryName;
      return name && name !== '—' ? name : 'לא מסווג';
    },
    [classMode],
  );

  // ── Month index from date string ──────────────────────────────────────────
  const monthOf = (dateStr: string) =>
    Math.max(0, Math.min(11, parseInt(dateStr.slice(5, 7), 10) - 1));

  // ── Build matrix rows ─────────────────────────────────────────────────────
  const matrixRows = useMemo<MatrixRow[]>(() => {
    // Group ForecastRows by category label
    const catMap = new Map<string, ForecastRow[][]>();   // category → [month0..11][]
    const catAll = new Map<string, ForecastRow[]>();     // category → all rows

    for (const row of yearRows) {
      const cat = labelOf(row);
      const mi  = monthOf(row.effectiveDate);

      // per-month buckets
      if (!catMap.has(cat)) {
        catMap.set(cat, Array.from({ length: 12 }, () => []));
      }
      catMap.get(cat)![mi]!.push(row);

      // yearly bucket
      if (!catAll.has(cat)) catAll.set(cat, []);
      catAll.get(cat)!.push(row);
    }

    // Build MatrixRow array
    const rows: MatrixRow[] = [];
    for (const [category, rowsByMonth] of catMap) {
      const months        = rowsByMonth.map(b => b.reduce((s, r) => s + r.amount, 0));
      const incomeByMonth = rowsByMonth.map(b => b.filter(r => r.amount > 0).reduce((s, r) => s + r.amount, 0));
      const expenseByMonth= rowsByMonth.map(b => b.filter(r => r.amount < 0).reduce((s, r) => s + r.amount, 0));
      const total         = months.reduce((s, v) => s + v, 0);
      const yearAll       = catAll.get(category) ?? [];
      const incomeTotal   = yearAll.filter(r => r.amount > 0).reduce((s, r) => s + r.amount, 0);
      const expenseTotal  = yearAll.filter(r => r.amount < 0).reduce((s, r) => s + r.amount, 0);
      rows.push({
        category,
        months,
        incomeByMonth,
        expenseByMonth,
        total,
        incomeTotal,
        expenseTotal,
        colorIndex: 0,                         // filled in after sort
        rowsByMonth,
        allRows: yearAll,
      });
    }

    // Sort: accounting mode → fixed P&L order; cashflow mode → "שוטף" pinned first, rest by absolute total
    const CASHFLOW_PINNED_TOP = 'שוטף – פעילות עסקית';

    if (classMode === 'accounting') {
      rows.sort((a, b) => {
        const pa = accountingCategoryPriority(a.category);
        const pb = accountingCategoryPriority(b.category);
        if (pa !== pb) return pa - pb;
        // Unlisted categories among themselves: largest absolute total first
        return Math.abs(b.total) - Math.abs(a.total);
      });
    } else {
      // Cashflow view: "שוטף – פעילות עסקית" always first, rest by largest absolute total
      rows.sort((a, b) => {
        const aTop = a.category === CASHFLOW_PINNED_TOP ? 0 : 1;
        const bTop = b.category === CASHFLOW_PINNED_TOP ? 0 : 1;
        if (aTop !== bTop) return aTop - bTop;
        return Math.abs(b.total) - Math.abs(a.total);
      });
    }
    rows.forEach((r, i) => { r.colorIndex = i % PALETTE.length; });

    return rows;
  }, [yearRows, labelOf, classMode]);

  // ── Column totals ─────────────────────────────────────────────────────────
  const columnTotals = useMemo<number[]>(() => {
    const totals = new Array<number>(12).fill(0);
    for (const row of matrixRows) {
      row.months.forEach((v, i) => { totals[i] = (totals[i] ?? 0) + v; });
    }
    return totals;
  }, [matrixRows]);

  const grandTotal = useMemo(
    () => columnTotals.reduce((s, v) => s + v, 0),
    [columnTotals],
  );

  // ── Pie data ──────────────────────────────────────────────────────────────
  const pieData = useMemo<PieSlice[]>(() =>
    matrixRows
      .filter(r => r.total !== 0)
      .map(r => ({
        name:  r.category,
        value: Math.abs(r.total),
        raw:   r.total,
        color: PALETTE[r.colorIndex] ?? '#a3a3a3',
      })),
    [matrixRows],
  );

  // ── Drill-down openers ────────────────────────────────────────────────────

  /** Open drill-down for a category × month cell with a specific row filter */
  const openMonthDrill = useCallback(
    (row: MatrixRow, monthIndex: number, rowFilter: RowFilter = 'all') => {
      const bucket = row.rowsByMonth[monthIndex] ?? [];
      // Check that the chosen slice actually has rows
      const hasRows =
        rowFilter === 'income'  ? bucket.some(r => r.amount > 0) :
        rowFilter === 'expense' ? bucket.some(r => r.amount < 0) :
        bucket.length > 0;
      if (!hasRows) return;
      setDrillTarget({
        category:   row.category,
        monthIndex,
        rows:       bucket,          // pass ALL rows; modal filters by rowFilter
        rowFilter,
        year:       selectedYear,
        color:      PALETTE[row.colorIndex] ?? '#a3a3a3',
      });
    },
    [selectedYear],
  );

  /** Open drill-down for a category's full-year cell with a specific row filter */
  const openYearDrill = useCallback(
    (row: MatrixRow, rowFilter: RowFilter = 'all') => {
      const hasRows =
        rowFilter === 'income'  ? row.allRows.some(r => r.amount > 0) :
        rowFilter === 'expense' ? row.allRows.some(r => r.amount < 0) :
        row.allRows.length > 0;
      if (!hasRows) return;
      setDrillTarget({
        category:   row.category,
        monthIndex: null,
        rows:       row.allRows,     // pass ALL rows; modal filters by rowFilter
        rowFilter,
        year:       selectedYear,
        color:      PALETTE[row.colorIndex] ?? '#a3a3a3',
      });
    },
    [selectedYear],
  );

  // ── Expand / collapse state ──────────────────────────────────────────────
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const toggleExpand = useCallback((category: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  }, []);

  // Collapse all when year or classMode changes so stale open rows don't persist
  useEffect(() => {
    setExpandedCategories(new Set());
  }, [selectedYear, classMode]);

  // ── Formatting helper ─────────────────────────────────────────────────────
  const fmtCell = (v: number) => (v === 0 ? null : ILS.format(v));

  // ── Sticky table ref ──────────────────────────────────────────────────────
  const tableRef = useRef<HTMLDivElement>(null);

  // ── Guard ─────────────────────────────────────────────────────────────────
  if (!activeCompanyId) return null;

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Drill-down modal (portal-like, rendered at component root) ── */}
      <CategoryDrillDownModal
        target={drillTarget}
        onClose={() => setDrillTarget(null)}
      />

      <div className="space-y-4" dir="rtl">

        {/* ════════════════════════════════════════════════════════════════ */}
        {/* Section Header                                                   */}
        {/* ════════════════════════════════════════════════════════════════ */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">

            {/* Title */}
            <div>
              <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <BarChart2 className="w-5 h-5 text-primary-600" />
                סיכום לפי סיווג
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                ניתוח שנתי — לחץ על כל תא לפירוט התנועות
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">

              {/* Year selector */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-500">שנה</label>
                <div className="relative">
                  <select
                    value={selectedYear}
                    onChange={e => setSelectedYear(Number(e.target.value))}
                    className="appearance-none rounded-lg border border-gray-300 bg-white pl-7 pr-3 py-2
                      text-sm text-gray-800 font-semibold
                      focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {YEAR_OPTIONS.map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Classification toggle */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-500">סיווג</label>
                <div className="flex items-center bg-gray-100 rounded-lg p-1 gap-1">
                  <button
                    onClick={() => setClassMode('cashflow')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold
                      transition-all whitespace-nowrap ${
                      classMode === 'cashflow'
                        ? 'bg-white shadow-sm text-primary-700 border border-primary-200'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Tag className="w-3 h-3" />
                    סיווג תזרים
                  </button>
                  <button
                    onClick={() => setClassMode('accounting')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold
                      transition-all whitespace-nowrap ${
                      classMode === 'accounting'
                        ? 'bg-white shadow-sm text-primary-700 border border-primary-200'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <BookOpen className="w-3 h-3" />
                    סיווג הנהלת חשבונות
                  </button>
                </div>
              </div>

              {/* Refresh */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-500 opacity-0 select-none" aria-hidden>פ</label>
                <button
                  onClick={() => setRefreshKey(k => k + 1)}
                  title="רענן"
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white
                    text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  <Loader2 className={`w-4 h-4 ${loading ? 'animate-spin text-primary-500' : 'text-gray-400'}`} />
                  <span className="hidden sm:inline text-xs">רענן</span>
                </button>
              </div>
            </div>
          </div>

          {errorMsg && (
            <div className="mt-3 flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              {errorMsg}
            </div>
          )}
        </div>

        {/* ════════════════════════════════════════════════════════════════ */}
        {/* Content: Doughnut + Matrix                                       */}
        {/* ════════════════════════════════════════════════════════════════ */}
        {loading ? (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
          </div>
        ) : matrixRows.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col items-center justify-center h-48 text-gray-400 gap-3">
            <BarChart2 className="w-12 h-12 text-gray-300" />
            <p className="text-sm font-medium">אין נתונים לשנת {selectedYear}</p>
            <p className="text-xs">בחר שנה אחרת או הוסף תנועות</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="flex flex-col xl:flex-row gap-6">

              {/* ── LEFT: Doughnut chart ──────────────────────────────── */}
              <div className="xl:w-[340px] flex-shrink-0 flex flex-col items-center">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 self-start">
                  {selectedYear} — חלוקה שנתית
                </h3>

                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={70}
                      outerRadius={115}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                      isAnimationActive
                    >
                      {pieData.map((slice, idx) => (
                        <Cell key={idx} fill={slice.color} stroke="white" strokeWidth={1.5} />
                      ))}
                    </Pie>
                    <RechartsTooltip content={<PieTooltipContent />} />
                  </PieChart>
                </ResponsiveContainer>

                {/* Legend */}
                <div className="mt-2 w-full max-h-52 overflow-y-auto space-y-1.5 pr-1">
                  {pieData.map((slice, idx) => (
                    <div key={idx} className="flex items-center justify-between gap-2 text-xs">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span
                          className="inline-block w-2.5 h-2.5 rounded-sm flex-shrink-0"
                          style={{ backgroundColor: slice.color }}
                        />
                        <span className="truncate text-gray-700 font-medium">{slice.name}</span>
                      </div>
                      <span className={`font-bold tabular-nums flex-shrink-0 ${amountClass(slice.raw)}`}>
                        {ILS.format(slice.raw)}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Grand total pill */}
                <div className={`mt-4 rounded-full px-5 py-2 text-sm font-extrabold tabular-nums border ${
                  grandTotal >= 0
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                    : 'bg-red-50 border-red-200 text-red-800'
                }`}>
                  סה״כ {selectedYear}: {ILS2.format(grandTotal)}
                </div>
              </div>

              {/* ── RIGHT: Matrix table ────────────────────────────────── */}
              <div className="flex-1 min-w-0" ref={tableRef}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-700">
                    טבלת חודשים × קטגוריה
                  </h3>
                  <p className="text-[11px] text-blue-500 font-medium flex items-center gap-1">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400" />
                    לחץ על ▶ להרחבה · לחץ על תא לפירוט
                  </p>
                </div>

                <div className="overflow-x-auto rounded-lg border border-gray-200">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        {/* Category header — sticky right (RTL) */}
                        <th className="sticky right-0 z-10 bg-gray-50 px-3 py-2.5 text-right
                          text-xs font-bold text-gray-600 border-l border-gray-200 whitespace-nowrap min-w-[150px]">
                          קטגוריה
                        </th>
                        {/* Month headers */}
                        {MONTH_NAMES_SHORT.map((name, idx) => (
                          <th key={idx}
                            className="px-2 py-2.5 text-center text-xs font-semibold text-gray-500 whitespace-nowrap min-w-[72px]">
                            {name}
                          </th>
                        ))}
                        {/* Total header */}
                        <th className="px-3 py-2.5 text-center text-xs font-bold text-gray-700
                          bg-gray-100 border-r border-gray-200 whitespace-nowrap min-w-[90px]">
                          סה״כ
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-100">
                      {matrixRows.map((row, rowIdx) => {
                        const isExpanded = expandedCategories.has(row.category);
                        const rowBg      = rowIdx % 2 === 1 ? 'rgb(249 250 251 / 0.5)' : 'white';

                        return (
                          <React.Fragment key={row.category}>
                            {/* ═══ PARENT ROW ═══ */}
                            <tr
                              className={`transition-colors ${
                                rowIdx % 2 === 1 ? 'bg-gray-50/30' : 'bg-white'
                              }`}
                            >
                              {/* Category cell — sticky, with expand toggle */}
                              <td
                                className="sticky right-0 z-10 px-2 py-2 text-right font-semibold
                                  text-gray-800 border-l border-gray-200 whitespace-nowrap"
                                style={{ backgroundColor: rowBg }}
                              >
                                <div className="flex items-center gap-1.5">
                                  {/* Expand/collapse chevron */}
                                  <button
                                    onClick={() => toggleExpand(row.category)}
                                    className="flex-shrink-0 p-0.5 rounded text-gray-400
                                      hover:text-gray-700 hover:bg-gray-200 transition-colors"
                                    title={isExpanded ? 'כווץ' : 'הרחב הכנסות/הוצאות'}
                                    aria-label={isExpanded ? 'כווץ' : 'הרחב'}
                                  >
                                    {isExpanded
                                      ? <ChevronDown  className="w-3.5 h-3.5" />
                                      : <ChevronRight className="w-3.5 h-3.5" />}
                                  </button>

                                  <span
                                    className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: PALETTE[row.colorIndex] }}
                                  />
                                  <span className="truncate max-w-[120px]" title={row.category}>
                                    {row.category}
                                  </span>
                                </div>
                              </td>

                              {/* Monthly net cells */}
                              {row.months.map((val, mIdx) => {
                                const bucket  = row.rowsByMonth[mIdx] ?? [];
                                const hasData = bucket.length > 0;
                                return (
                                  <td
                                    key={mIdx}
                                    onClick={() => hasData && openMonthDrill(row, mIdx, 'all')}
                                    title={hasData ? `${row.category} — ${MONTH_NAMES_LONG[mIdx]} ${selectedYear} (נטו): לחץ לפירוט` : undefined}
                                    className={[
                                      'px-2 py-2 text-center tabular-nums font-medium whitespace-nowrap transition-all duration-100',
                                      amountClass(val),
                                      hasData ? 'cursor-pointer hover:bg-blue-50 hover:ring-1 hover:ring-inset hover:ring-blue-200 hover:font-bold rounded-sm' : '',
                                    ].join(' ')}
                                  >
                                    {fmtCell(val) ?? <span className="text-gray-200 select-none">—</span>}
                                  </td>
                                );
                              })}

                              {/* Yearly net total */}
                              <td
                                onClick={() => openYearDrill(row, 'all')}
                                title={`${row.category} — נטו ${selectedYear}: לחץ לפירוט`}
                                className={[
                                  'px-3 py-2 text-center tabular-nums font-bold whitespace-nowrap border-r border-gray-200',
                                  'cursor-pointer transition-all duration-100',
                                  'hover:bg-blue-50 hover:ring-1 hover:ring-inset hover:ring-blue-300 hover:font-extrabold rounded-sm',
                                  amountClass(row.total),
                                ].join(' ')}
                              >
                                {ILS.format(row.total)}
                              </td>
                            </tr>

                            {/* ═══ INCOME SUB-ROW ═══ */}
                            {isExpanded && (
                              <tr
                                className="bg-emerald-50/40 border-r-2 border-emerald-300"
                              >
                                <td
                                  className="sticky right-0 z-10 bg-emerald-50/70 pr-3 pl-2 py-1.5
                                    text-right border-l border-gray-200 whitespace-nowrap"
                                >
                                  <div className="flex items-center gap-1.5 pr-5">
                                    <TrendingUp className="w-3 h-3 text-emerald-600 flex-shrink-0" />
                                    <span className="text-[11px] font-semibold text-emerald-700">
                                      הכנסות
                                    </span>
                                  </div>
                                </td>

                                {row.incomeByMonth.map((val, mIdx) => {
                                  const bucket   = row.rowsByMonth[mIdx] ?? [];
                                  const hasIncome = bucket.some(r => r.amount > 0);
                                  return (
                                    <td
                                      key={mIdx}
                                      onClick={() => hasIncome && openMonthDrill(row, mIdx, 'income')}
                                      title={hasIncome ? `הכנסות — ${MONTH_NAMES_LONG[mIdx]}: לחץ לפירוט` : undefined}
                                      className={[
                                        'px-2 py-1.5 text-center tabular-nums text-[11px] font-medium whitespace-nowrap transition-all duration-100',
                                        val > 0 ? 'text-emerald-700' : 'text-gray-200',
                                        hasIncome ? 'cursor-pointer hover:bg-emerald-100 hover:font-bold rounded-sm' : '',
                                      ].join(' ')}
                                    >
                                      {val > 0 ? ILS.format(val) : <span className="select-none">—</span>}
                                    </td>
                                  );
                                })}

                                <td
                                  onClick={() => openYearDrill(row, 'income')}
                                  title={`הכנסות — ${row.category} ${selectedYear}: לחץ לפירוט`}
                                  className={[
                                    'px-3 py-1.5 text-center tabular-nums text-[11px] font-bold whitespace-nowrap border-r border-gray-200',
                                    row.incomeTotal > 0 ? 'text-emerald-700 cursor-pointer hover:bg-emerald-100 hover:font-extrabold rounded-sm transition-all' : 'text-gray-200',
                                  ].join(' ')}
                                >
                                  {row.incomeTotal > 0 ? ILS.format(row.incomeTotal) : '—'}
                                </td>
                              </tr>
                            )}

                            {/* ═══ EXPENSE SUB-ROW ═══ */}
                            {isExpanded && (
                              <tr
                                className="bg-red-50/30 border-r-2 border-red-300"
                              >
                                <td
                                  className="sticky right-0 z-10 bg-red-50/50 pr-3 pl-2 py-1.5
                                    text-right border-l border-gray-200 whitespace-nowrap"
                                >
                                  <div className="flex items-center gap-1.5 pr-5">
                                    <TrendingDown className="w-3 h-3 text-red-600 flex-shrink-0" />
                                    <span className="text-[11px] font-semibold text-red-700">
                                      הוצאות
                                    </span>
                                  </div>
                                </td>

                                {row.expenseByMonth.map((val, mIdx) => {
                                  const bucket    = row.rowsByMonth[mIdx] ?? [];
                                  const hasExpense = bucket.some(r => r.amount < 0);
                                  return (
                                    <td
                                      key={mIdx}
                                      onClick={() => hasExpense && openMonthDrill(row, mIdx, 'expense')}
                                      title={hasExpense ? `הוצאות — ${MONTH_NAMES_LONG[mIdx]}: לחץ לפירוט` : undefined}
                                      className={[
                                        'px-2 py-1.5 text-center tabular-nums text-[11px] font-medium whitespace-nowrap transition-all duration-100',
                                        val < 0 ? 'text-red-700' : 'text-gray-200',
                                        hasExpense ? 'cursor-pointer hover:bg-red-100 hover:font-bold rounded-sm' : '',
                                      ].join(' ')}
                                    >
                                      {val < 0 ? ILS.format(val) : <span className="select-none">—</span>}
                                    </td>
                                  );
                                })}

                                <td
                                  onClick={() => openYearDrill(row, 'expense')}
                                  title={`הוצאות — ${row.category} ${selectedYear}: לחץ לפירוט`}
                                  className={[
                                    'px-3 py-1.5 text-center tabular-nums text-[11px] font-bold whitespace-nowrap border-r border-gray-200',
                                    row.expenseTotal < 0 ? 'text-red-700 cursor-pointer hover:bg-red-100 hover:font-extrabold rounded-sm transition-all' : 'text-gray-200',
                                  ].join(' ')}
                                >
                                  {row.expenseTotal < 0 ? ILS.format(row.expenseTotal) : '—'}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>

                    {/* Footer — column totals (NOT individually clickable) */}
                    <tfoot>
                      <tr className="border-t-2 border-gray-300 bg-gray-100">
                        <td className="sticky right-0 z-10 bg-gray-100 px-3 py-2.5
                          text-right font-bold text-gray-700 border-l border-gray-200 whitespace-nowrap">
                          סה״כ חודשי
                        </td>
                        {columnTotals.map((val, mIdx) => (
                          <td key={mIdx}
                            className={`px-2 py-2.5 text-center tabular-nums font-bold whitespace-nowrap ${amountClass(val)}`}>
                            {fmtCell(val) ?? <span className="text-gray-300 select-none">—</span>}
                          </td>
                        ))}
                        <td className={`px-3 py-2.5 text-center tabular-nums font-extrabold whitespace-nowrap
                          border-r border-gray-300 bg-gray-200 ${amountClass(grandTotal)}`}>
                          {ILS.format(grandTotal)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Row count note */}
                <p className="mt-2 text-[11px] text-gray-400 text-left">
                  {matrixRows.length} קטגוריות · {yearRows.length} תנועות בשנת {selectedYear}
                </p>
              </div>

            </div>
          </div>
        )}
      </div>
    </>
  );
}
